import get from 'lodash/get';
import { Stack, ThemeProvider } from '@mui/material';
import { Control, FieldError, FieldValues, useController } from 'react-hook-form';

import { theme } from '../src/customTheme.tsx';
import { FormFieldProps, FormFieldType } from './types/FormField.ts';

import { TextField } from './fields/TextField.tsx';
import { RadioField } from './fields/RadioField.tsx';
import { DropdownField } from './fields/DropdownField.tsx';
import { DatePickerField } from './fields/DatePickerField.tsx';
import { CheckboxSetField } from './fields/CheckboxSetField.tsx';
import { FileUploaderField } from './fields/FileUploaderField.tsx';
import { PictureUploaderField } from './fields/PictureUploaderField.tsx';
import { TagsAutocompleteField } from './fields/TagsAutocompleteField.tsx';
import { AddressAutocompleteField } from './fields/AddressAutocompleteFields.tsx';
import NumberField from './fields/NumberField.tsx';
import { SelectionRangeTableField } from './fields/SelectionRangeTableField.tsx';
import { AutocompleteField } from './fields/AutocompleteField.tsx';
import { CheckboxField } from './fields/CheckboxField.tsx';

export const FormField = <T extends FieldValues>(props: FormFieldProps<T>) => {
  const { label = '', name, control, type, rules, disabled = false, readOnly } = props;
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController<T>({ name, control, rules });

  const commonProps = { name, label, disabled, readOnly, value, onChange, error: get(errors, name) as FieldError };

  let component: JSX.Element;

  switch (type) {
    case FormFieldType.Text:
      component = (
        <TextField
          {...commonProps}
          rows={props.rows}
          multiline={props.multiline}
          maxLength={props.maxLength}
          maxWords={props.maxWords}
          textSuffix={props.textSuffix}
          helperText={props.helperText}
          placeholder={props.placeholder}
        />
      );
      break;
    case FormFieldType.Date:
      component = (
        <DatePickerField
          {...commonProps}
          min={props.min}
          max={props.max}
          value={new Date(value)}
          maxPeriod={props.maxPeriod}
          maxDirection={props.maxDirection}
          displayDays={props.displayDays}
        />
      );
      break;
    case FormFieldType.Radio:
      component = (
        <RadioField
          {...commonProps}
          options={props.options}
          direction={props.direction}
          control={control as Control}
          itemWrapperProps={props.itemWrapperProps}
        />
      );
      break;
    case FormFieldType.Checkboxset:
      component = (
        <CheckboxSetField
          {...commonProps}
          options={props.options}
          direction={props.direction}
          control={control as Control}
          itemWrapperProps={props.itemWrapperProps}
        />
      );
      break;
    case FormFieldType.Checkbox:
      component = <CheckboxField {...commonProps} />;
      break;
    case FormFieldType.Dropdown:
      component = <DropdownField {...commonProps} options={props.options} placeholderValue={props.placeholderValue} />;
      break;
    case FormFieldType.TagsAutocomplete:
      component = <TagsAutocompleteField {...commonProps} groupedOptions={props.groupedOptions} />;
      break;
    case FormFieldType.PictureUploader:
      component = <PictureUploaderField {...commonProps} id={props.id} readOnly={props.readOnly} aspectRatio={props.aspectRatio} />;
      break;
    case FormFieldType.FileUploader:
      component = (
        <FileUploaderField
          {...commonProps}
          id={props.id}
          readOnly={props.readOnly}
          allowMultiple={props.allowMultiple}
          acceptedMimeTypes={props.acceptedMimeTypes}
        />
      );
      break;
    case FormFieldType.AddressAutocomplete:
      component = (
        <AddressAutocompleteField
          {...commonProps}
          googleMapsAPIKey={props.googleMapsAPIKey}
          restrictToUS={props.restrictToUS}
          addressInputs={props.addressInputs}
        />
      );
      break;
    case FormFieldType.SelectionRangeTable:
      component = <SelectionRangeTableField {...commonProps} options={props.options} />;
      break;
    case FormFieldType.Number:
      component = <NumberField {...commonProps} min={props.min} max={props.max} textSuffix={props.textSuffix} helperText={props.helperText} />;
      break;
    case FormFieldType.Autocomplete:
      component = <AutocompleteField {...commonProps} groupedOptions={props.groupedOptions} disabledOptions={props.disabledOptions} />;
      break;
    default:
      component = <></>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={0.5}>{component}</Stack>
    </ThemeProvider>
  );
};
